import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Questionnaire, Question } from 'types/questionnaire/types';
import QuestionRenderer from './QuestionRenderer';

type AnswersMap = Record<string, unknown>;

interface QuestionnaireWizardProps {
  questionnaire: Questionnaire;
  initialAnswers?: AnswersMap;
  onComplete: (answers: AnswersMap) => void;
  isSubmitting?: boolean;
}

const QuestionnaireWizard: React.FC<QuestionnaireWizardProps> = ({
  questionnaire,
  initialAnswers,
  onComplete,
  isSubmitting,
}) => {
  const [answers, setAnswers] = useState<AnswersMap>(initialAnswers ?? {});
  const [currentQuestionId, setCurrentQuestionId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [stopWarning, setStopWarning] = useState(false);

  const allQuestions = useMemo<Question[]>(() => {
    return questionnaire.pages.flatMap((page) => page.questions);
  }, [questionnaire.pages]);

  const isQuestionVisible = useCallback(
    (question: Question): boolean => {
      if (!question.showWhen) return true;
      return (
        answers[question.showWhen.questionId] !== undefined &&
        answers[question.showWhen.questionId] === question.showWhen.equals
      );
    },
    [answers]
  );

  const visibleQuestions = useMemo(() => allQuestions.filter(isQuestionVisible), [allQuestions, isQuestionVisible]);

  useEffect(() => {
    if (visibleQuestions.length === 0) {
      setCurrentQuestionId(null);
      return;
    }
    const stillVisible = visibleQuestions.find((q) => q.id === currentQuestionId);
    if (!stillVisible) {
      setCurrentQuestionId(visibleQuestions[0].id);
    }
  }, [visibleQuestions, currentQuestionId]);

  const currentQuestion = visibleQuestions.find((q) => q.id === currentQuestionId) ?? null;
  const currentIndex = currentQuestion ? visibleQuestions.findIndex((q) => q.id === currentQuestion.id) : -1;
  const totalQuestions = visibleQuestions.length;
  const isLastQuestion = currentIndex === totalQuestions - 1 && totalQuestions > 0;

  const handleAnswerChange = (value: unknown) => {
    if (!currentQuestion) return;
    const updated = { ...answers, [currentQuestion.id]: value };
    setAnswers(updated);
    setErrors((prev) => {
      const next = { ...prev };
      delete next[currentQuestion.id];
      return next;
    });
    if (currentQuestion.stopOnAnswer !== undefined && currentQuestion.stopOnAnswer === value) {
      setStopWarning(true);
      return;
    }
    setStopWarning(false);
    if (currentQuestion.type !== 'checkbox') {
      goToNextQuestion(updated);
    }
  };

  const validateCurrentQuestion = (answersSnapshot: AnswersMap = answers): boolean => {
    if (!currentQuestion) return true;
    if (currentQuestion.required) {
      const val = answersSnapshot[currentQuestion.id];
      const isEmpty =
        val === undefined ||
        val === null ||
        (typeof val === 'string' && val.trim() === '') ||
        (Array.isArray(val) && val.length === 0);
      if (isEmpty) {
        setErrors((prev) => ({ ...prev, [currentQuestion.id]: 'This field is required' }));
        return false;
      }
    }
    return true;
  };

  const goToNextQuestion = (answersSnapshot: AnswersMap = answers) => {
    if (!validateCurrentQuestion(answersSnapshot)) return;
    if (isLastQuestion) {
      if (isSubmitting) return;
      onComplete(answersSnapshot);
      return;
    }
    const nextQuestion = visibleQuestions[currentIndex + 1];
    if (nextQuestion) setCurrentQuestionId(nextQuestion.id);
  };

  if (!currentQuestion) {
    return <div>No questions available</div>;
  }

  const progressPercent = totalQuestions > 0 ? ((currentIndex + 1) / totalQuestions) * 100 : 0;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-sm text-brand-black/70">
          Question {currentIndex + 1} of {totalQuestions}
        </p>
      </div>

      <div className="h-2 w-full rounded-full bg-gray-200">
        <div
          className="h-2 rounded-full bg-talo-dark-green transition-all duration-200"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      <QuestionRenderer
        key={currentQuestion.id}
        question={currentQuestion}
        value={answers[currentQuestion.id]}
        onChange={handleAnswerChange}
        onAdvance={() => goToNextQuestion(answers)}
        error={errors[currentQuestion.id]}
        stopWarning={stopWarning}
        isSubmitting={isSubmitting && isLastQuestion}
      />
    </div>
  );
};

export default QuestionnaireWizard;
